import { Component, effect, inject, input, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { EChartsOption } from 'echarts';
import { NgxEchartsDirective, provideEcharts } from 'ngx-echarts';

import { RegisterComponent } from '../../components/component-lookup';
import { VisualBaseComponent } from '../visual-base.component';
import { DecayMetric, DecayMetricData } from '../../services/metric.service';
import { DateRange, StateService } from '../../services/state.service';
import { VisualService } from '../../services/visual.service';
import { DateService } from '../../services/date.service';
import { M2Data, RateService, RateDefinition } from '../../services/rate.service';
import { ChartInfoBoxComponent } from '../chart-info-box/chart-info-box.component';

@RegisterComponent('DecayBasicComponent')
@Component({
  selector: 'app-decay-basic',
  standalone: true,
  imports: [CommonModule, FormsModule, NgxEchartsDirective, ChartInfoBoxComponent],
  templateUrl: './decay-basic.component.html',
  styleUrl: './decay-basic.component.scss',
  providers: [provideEcharts()]
})
export class DecayBasicComponent extends VisualBaseComponent {
  readonly id = input.required<number>();
  readonly institution = inject(StateService).institution;
  readonly filteredGroup = inject(StateService).filteredGroup;
  readonly dateRange = inject(StateService).dateRange;
  readonly rateService = inject(RateService);
  private dateService = inject(DateService);
  readonly decayChartType = input<string>('m2Balance');
  readonly dialogInformationId = input<string>('');


  private xAxisData: string[] = [];
  private yAxisData1: (number)[] = [];
  private yAxisData2: (number)[] = [];
  private yAxisData3: (number)[] = [];
  private yAxisData1Lower: number = 0;
  private yAxisData2Lower: number = 0;

  readonly isM2Balance = computed(() => this.decayChartType() === 'm2Balance');


  override initOptions = { height: 400 };
  override baseOptions: EChartsOption = {
    grid: {
      left: 10,
      right: 25,
      top: 50,
      bottom: 40,
      containLabel: true,
    },
    legend: {
      selectedMode: true,
    },
    title: {
      show: false,
    },
    xAxis: {
      type: 'category',
      show: true,
      axisLabel: {
        color: '#243b5b',
      },
    },
    yAxis: {
      type: 'value',
      show: true,
      axisLabel: {
        color: '#243b5b',
      },
    },
    axisLabel: {
      color: '#002f8e',
    },
    series: [
      {
        type: 'line',
        symbol: 'none',
      },
      {
        type: 'line',
        symbol: 'none',
      },
      {
        type: 'line',
        symbol: 'none',
      },
    ],
  };
  override tooltipOptions: EChartsOption = {
    tooltip: {
      trigger: 'axis',
      valueFormatter: (value) =>
        value != null ? VisualService.getValueDisplay(Math.trunc(Number(value)), 'USDM') : '--',

    },
  };
  override printOptions: EChartsOption = {
    animation: false,
    series: [
      {
        type: 'line',
        symbol: 'none',
      },
      {
        type: 'line',
        symbol: 'none',
        symbolSize: 7,
        lineStyle: {
          type: [20, 5],
          dashOffset: 5,
        },
      },
    ],
  };

  noData = false;
  futureDates: Date[] = [];
  displayTypeMetric: string | undefined;
  rateDefinition: RateDefinition | undefined;
  attributionDate: Date | undefined;

  constructor() {
    super();
    effect(() => {
      const dateRange = this.dateRange();
      const metric = this.metricService.selectedDecayMetric();
      this.displayTypeMetric = metric?.displayType;
      if (this.chartInit() && dateRange && metric) {
        this.dateService.getFutureDates(this.institution().institutionType).subscribe((result) => {
          this.futureDates = result.labels;
        });
        this.rateService.getRateDefinition("WM2NS").subscribe((result) => {
          this.rateDefinition = result;
          this.attributionDate = this.subtractQuarters(result.rateEndDate, result.lagValue)
        });
        this.updateVisual(dateRange, metric);
      }
    });
  }

  public updateVisual(dateRange: DateRange, metric: DecayMetric) {
    this.metricService.getDecayMetricData(this.institution().iid, metric.metricId)?.subscribe((decayData) => {
      this.rateService.getM2Data().subscribe((m2Data) => {

        if (decayData.filter((v) => v != null).length == 0) {
          this.echartsInstance?.clear();
          this.chartOptions = this.noDataOptions;
          this.noData = true;
        } else {
          if (this.noData) {
            this.setOptions();
            this.noData = false;
          }
          const cleanedData = this.trimLeadingZeros(decayData);
          const medians = this.calculateGrowtRateMedians(cleanedData.filteredData, dateRange.startDate, dateRange.endDate);
          const filteredDataRange = this.filterByDateRange(cleanedData.filteredData, dateRange.startDate, dateRange.endDate);
          const stableBalances = this.calculateStableBalance(filteredDataRange, medians.minGrowthRateMedian, medians.maxGrowthRateMedian, dateRange.startDate)

          if (this.isM2Balance()) {
            this.updateM2States(decayData, m2Data, stableBalances ? stableBalances : [], dateRange)
          }
          else if (this.decayChartType() == 'adustedBalance') {
            this.updateAdjustedStates(decayData, stableBalances ? stableBalances : [], dateRange)
          }

          this.updateOptions = {
            xAxis: {
              data: this.xAxisData,
            },
            yAxis: [
              {
                type: 'value',
                name: this.isM2Balance() ? 'Bank (\'000\'s)' : '',
                min: this.yAxisData1Lower * 0.85, // Minimum value for the first y-axis
                axisLabel: {
                  formatter: (value: number) => {
                    // Hide the label for the minimum value
                    if (value === this.yAxisData1Lower * 0.85) {
                      return ''; // Hide the label for the minimum value
                    }
                    // Add comma separators to the number
                    return value.toLocaleString();
                  },
                },
              },
              {
                type: 'value',
                name: this.isM2Balance() ? 'M2 (in Trillions)' : '',
                min: this.yAxisData2Lower * 0.90, // Minimum value for the second y-axis
                splitLine: {
                  show: false, // Hide grid lines for the right y-axis
                },
                axisLabel: {
                  formatter: (value: number) => {
                    // Hide the label for the minimum value
                    if (value === this.yAxisData2Lower * 0.90) {
                      return ''; // Hide the label for the minimum value
                    }
                    // Add comma separators to the number
                    return value.toLocaleString();
                  },
                  color: '#333'
                },
              },
            ],
            series: [
              {
                name: 'Actual Balance',
                data: this.yAxisData1,
                color: this.colors[0],
                symbol: 'line',
                symbolSize: 0,
                yAxisIndex: 0,
              },
              {
                name: this.isM2Balance() ? 'M2' : 'Stable Balance',
                data: this.yAxisData2,
                color: this.colors[1],
                areaStyle: {
                  color: this.isM2Balance() ? 'rgba(255, 255, 255, 0)' : undefined, // Fill color (light blue with transparency)
                },
                lineStyle: {
                  type: 'dashed',
                },
                symbol: 'line',
                symbolSize: 0,
                yAxisIndex: this.isM2Balance() ? 1 : undefined,
                tooltip: {
                  trigger: 'axis',
                  valueFormatter: (value) =>
                    value != null ? VisualService.getValueDisplay(Math.trunc(Number(value)), this.isM2Balance() ? 'TRILLION' : 'USDM') : '--',
                },
              },
              {
                name: this.isM2Balance() ? 'Stable Balance' : undefined,
                data: this.yAxisData3,
                color: "#70cbff",
                symbol: 'line',
                symbolSize: 0,
              },
            ],
          };
        }
        this.echartsInstance?.hideLoading();
      });
    });
  }

  filterByDateRange<T extends { reportDate: Date | string }>(
    data: T[],
    startDate: Date,
    endDate: Date
  ): T[] {
    return data.filter(metric => {
      const reportDate = typeof metric.reportDate === 'string'
        ? new Date(metric.reportDate)
        : metric.reportDate;

      return reportDate >= startDate && reportDate <= endDate;
    });
  }

  subtractQuarters(date: Date | undefined, quarters: number): Date {
    const newDate = new Date(date ?? new Date());
    newDate.setMonth(newDate.getMonth() - quarters * 3);
    return newDate;
  }

  trimLeadingZeros(data: DecayMetricData[]): { filteredData: DecayMetricData[] } {

    const firstValidIndex = data.findIndex(item =>
      item.value !== null && item.value !== 0
    );

    return {
      filteredData: firstValidIndex === -1 ? [] : data.slice(firstValidIndex)
    };
  }

  calculateStableBalance(decayData: DecayMetricData[], minGrowthRateMedian: number | null, maxGrowthRateMedian: number | null, originalStartDate: Date) {
    if (minGrowthRateMedian && maxGrowthRateMedian) {
      const firstDate = decayData[0]?.reportDate;
      const wasTrimmed: boolean = new Date(firstDate) > new Date(originalStartDate);

      const growthRates = decayData
        .filter(item => item.growthRate != null && item != undefined)

      const stableBalances: { initialStableBalRateOfChange: number[]; stableBalance: number[] } = {
        initialStableBalRateOfChange: [],
        stableBalance: [],
      };

      let stableBalance: number = 0;
      let balRate: number = 0;
      let isFirst: boolean = true;
      let qtrsCount: number = 1;

      growthRates.forEach(item => {

        if (item.growthRate != null) {
          if (isFirst) {
            stableBalance = item.value!;
            balRate = 0;
            isFirst = false;
          }
          else {
            qtrsCount++;
            balRate =
              item.growthRate < minGrowthRateMedian
                ? minGrowthRateMedian
                : item.growthRate > maxGrowthRateMedian
                  ? maxGrowthRateMedian
                  : item.growthRate;


            stableBalance =
              Math.abs(item.growthRate) >= 0.25
                ? item.value! * 0.90
                : Math.min(item.value!, (stableBalance * (1 + balRate)))

            //Check this logic
            if (qtrsCount <= 5 && !wasTrimmed) {
              stableBalance = item.value!
            }
          }
          stableBalances.initialStableBalRateOfChange.push(balRate);
          stableBalances.stableBalance.push(stableBalance);
        }

      });
      return stableBalances.stableBalance;
    }
    return null;

  }

  calculateGrowtRateMedians(decayMetricData: DecayMetricData[], minDate: Date, maxDate: Date) {
    const isInDateRange = (dateStr: Date) =>
      new Date(dateStr) >= minDate && new Date(dateStr) <= maxDate;

    let values = decayMetricData
      .filter(item => isInDateRange(item.reportDate))
      .map(item => item.growthRate)
      .filter((val): val is number => val != null);

    //Do not include in Median calculation
    if (values[0] === 0) {
      values = values.slice(1);
    }

    values = values.sort((a, b) => a - b);

    const minValues = values.filter(val => val <= 0);
    const maxValues = values.filter(val => val > 0);

    return {
      minGrowthRateMedian: this.calculateMedian(minValues),
      maxGrowthRateMedian: this.calculateMedian(maxValues),

    };
  }

  calculateMedian = (arr: number[]) => {
    const orderedArray = arr.sort((a, b) => a - b);
    const len = orderedArray.length;
    if (len === 0) return null;
    const middle = Math.floor(len / 2);
    return len % 2 === 0 ? (orderedArray[middle - 1] + orderedArray[middle]) / 2 : orderedArray[middle];
  };

  updateM2States(decayData: DecayMetricData[], m2Data: M2Data[], stableBalances: number[], dateRange: DateRange) {

    const decayFiltered = this.filterByDateRange(decayData, dateRange.startDate, dateRange.endDate);
    const m2Filtered = this.filterByDateRange(m2Data, dateRange.startDate, dateRange.endDate);

    this.xAxisData = decayFiltered.map(dat => {
      const reportDate = new Date(dat.reportDate);
      if (isNaN(reportDate.getTime())) {
        throw new Error(`Invalid date: ${dat.reportDate}`);
      }

      return reportDate.toISOString().split('T')[0];
    });
    this.yAxisData1 = decayFiltered.map(item => (item.value ?? 0) / 1000);
    this.yAxisData2 = m2Filtered.map(value => value.value);
    this.yAxisData3 = stableBalances.map(val => val / 1000);

    //Add removed ceroes at the beggining 
    this.yAxisData3 = [
      ...Array(Math.max(0, this.yAxisData1.length - this.yAxisData3.length)).fill(0),
      ...this.yAxisData3
    ].slice(0, this.yAxisData1.length);

    this.yAxisData1Lower = Math.min(...this.yAxisData1);
    this.yAxisData2Lower = Math.min(...this.yAxisData2);


  }

  updateAdjustedStates(decayData: DecayMetricData[], stableBalances: number[], dateRange: DateRange) {

    const decayFiltered = this.filterByDateRange(decayData, dateRange.startDate, dateRange.endDate);

    this.xAxisData = decayFiltered.map(dat => {
      const reportDate = new Date(dat.reportDate);
      if (isNaN(reportDate.getTime())) {
        throw new Error(`Invalid date: ${dat.reportDate}`);
      }

      return reportDate.toISOString().split('T')[0];
    });
    this.yAxisData1 = decayFiltered.map(item => (item.value ?? 0) / 1000);
    this.yAxisData2 = stableBalances.map(val => val / 1000);

    //Add removed ceroes at the beggining 
    this.yAxisData2 = [
      ...Array(Math.max(0, this.yAxisData1.length - this.yAxisData2.length)).fill(0),
      ...this.yAxisData2
    ].slice(0, this.yAxisData1.length);

    this.yAxisData1Lower = Math.min(...this.yAxisData1);
    this.yAxisData2Lower = Math.min(...this.yAxisData2);
  }

}